import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Radio,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";
import StarIcon from "@mui/icons-material/Star";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { BookingFormState, VendorServiceItem, PricingType, fmtCAD } from "./bookingTypes";
import { calculateItemBasePrice } from "./pricingEngine";
import { API_URL } from "../../../config";

// ── Props ────────────────────────────────────────────────────────────────────
interface Props {
  form: BookingFormState;
  setForm: React.Dispatch<React.SetStateAction<BookingFormState>>;
}

// ── API types ────────────────────────────────────────────────────────────────
interface ApiVendor {
  id: number; name: string; company_name: string | null;
}
interface ApiVendorPrice {
  id: number; item_id: number; vendor_id: number;
  pricing_type: PricingType;
  vendor_price: number | string;
  dcc_price: number | string;
  min_hours: number | string | null;
  unit_label: string | null;
  is_default: boolean;
  image_url: string | null;
  vendor: ApiVendor | null;
}
interface ApiItem {
  id: number; name: string; description: string | null;
  unit: string | null; default_price: number | string | null;
  supported_pricing_types: PricingType[] | null;
  vendor_prices: ApiVendorPrice[];
  default_vendor_price: ApiVendorPrice | null;
}
interface ApiSubcategory {
  id: number; name: string; description: string | null;
  items?: ApiItem[];
}
interface ApiCategory {
  id: number; name: string; description: string | null;
  subcategories?: ApiSubcategory[];
  items?: ApiItem[];
}
interface ApiService {
  id: number; name: string; description: string | null;
  color: string | null; icon: string | null;
  categories?: ApiCategory[];
  items?: ApiItem[];
}

// Flattened item for the grid
interface FlatItem {
  item: ApiItem;
  service: ApiService;
  category: ApiCategory | null;
  subcategory: ApiSubcategory | null;
  categoryLabel: string; // for chip filter
}

// ── Pricing type labels ──────────────────────────────────────────────────────
const PRICING_LABELS: Record<PricingType, string> = {
  per_event:  "/ event",
  per_person: "/ person",
  per_hour:   "/ hour",
  per_plate:  "/ plate",
  per_unit:   "/ unit",
};

const PRICING_TYPE_OPTIONS: { value: PricingType; label: string }[] = [
  { value: "per_event", label: "Per Event" },
  { value: "per_person", label: "Per Person" },
  { value: "per_hour", label: "Per Hour" },
  { value: "per_plate", label: "Per Plate" },
  { value: "per_unit", label: "Per Unit" },
];

// ── API helper ───────────────────────────────────────────────────────────────
const apiFetch = async (path: string) => {
  const res = await fetch(`${API_URL}${path}`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
    },
  });
  return res.json();
};

// ═══════════════════════════════════════════════════════════════════════════════
//  MAIN: BookingAddOnServices
// ═══════════════════════════════════════════════════════════════════════════════
const BookingAddOnServices: React.FC<Props> = ({ form, setForm }) => {
  const [services, setServices] = useState<ApiService[]>([]);
  const [loading, setLoading] = useState(true);
  const [catFilter, setCatFilter] = useState<string>("All");
  const [search, setSearch] = useState("");
  const [expandedItemId, setExpandedItemId] = useState<number | null>(null);

  // ── Fetch service tree ─────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiFetch("/banquet/booking-items");
      if (res.code === 200 && Array.isArray(res.data)) {
        setServices(res.data);
      }
    } catch { /* silently ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Flatten service tree into a flat item list ─────────────────────────
  const flatItems = useMemo<FlatItem[]>(() => {
    const result: FlatItem[] = [];

    for (const service of services) {
      // Items directly under service
      if (service.items) {
        for (const item of service.items) {
          result.push({ item, service, category: null, subcategory: null, categoryLabel: service.name });
        }
      }

      // Items in categories
      if (service.categories) {
        for (const cat of service.categories) {
          if (cat.items) {
            for (const item of cat.items) {
              result.push({ item, service, category: cat, subcategory: null, categoryLabel: cat.name });
            }
          }

          // Items in subcategories
          if (cat.subcategories) {
            for (const subcat of cat.subcategories) {
              if (subcat.items) {
                for (const item of subcat.items) {
                  result.push({ item, service, category: cat, subcategory: subcat, categoryLabel: cat.name });
                }
              }
            }
          }
        }
      }
    }

    return result;
  }, [services]);

  // ── Category list for chips ────────────────────────────────────────────
  const categories = useMemo(() => {
    const cats = Array.from(new Set(flatItems.map((f) => f.categoryLabel)));
    return ["All", ...cats];
  }, [flatItems]);

  // ── Filtered items ────────────────────────────────────────────────────
  const filtered = useMemo(
    () =>
      flatItems.filter((f) => {
        const matchCat = catFilter === "All" || f.categoryLabel === catFilter;
        const matchText =
          !search || f.item.name.toLowerCase().includes(search.toLowerCase());
        return matchCat && matchText;
      }),
    [flatItems, catFilter, search]
  );

  // ── Helpers ──────────────────────────────────────────────────────────────
  const isItemSelected = (itemId: number) =>
    form.vendorServices.some((vs) => vs.itemId === itemId);

  const getSelectedForItem = (itemId: number) =>
    form.vendorServices.find((vs) => vs.itemId === itemId);

  // ── Smart pricing type suggestion ──────────────────────────────────────
  const suggestPricingType = useCallback((item: ApiItem): PricingType => {
    const types = Array.from(new Set((item.vendor_prices || []).map(vp => vp.pricing_type)));
    const guestCount = Number(form.guestCount) || 0;
    if (guestCount > 50 && types.includes("per_person")) return "per_person";
    if (types.includes("per_hour") && !types.includes("per_event")) return "per_hour";
    if (types.includes("per_event")) return "per_event";
    return types[0] ?? "per_event";
  }, [form.guestCount]);

  // ── Select vendor for an item ───────────────────────────────────────────
  const selectVendorForItem = (
    service: ApiService, category: ApiCategory | null,
    subcategory: ApiSubcategory | null, item: ApiItem,
    vp: ApiVendorPrice,
  ) => {
    const rate = Number(vp.dcc_price);
    const guestCount = Number(form.guestCount) || 100;
    const minHours = Number(vp.min_hours) || 1;

    let qty = 1;
    let hrs = 1;
    switch (vp.pricing_type) {
      case "per_person":
      case "per_plate":
        qty = guestCount;
        break;
      case "per_hour":
        hrs = minHours;
        break;
      case "per_unit":
        qty = 1;
        break;
      case "per_event":
      default:
        qty = 1;
        break;
    }

    const basePrice = calculateItemBasePrice(rate, qty, hrs, vp.pricing_type);

    setForm((prev) => {
      const filtered = prev.vendorServices.filter((vs) => vs.itemId !== item.id);
      return {
        ...prev,
        vendorServices: [
          ...filtered,
          {
            itemId: item.id,
            itemName: item.name,
            serviceId: service.id,
            serviceName: service.name,
            serviceCategory: service.name.toLowerCase().replace(/\s+/g, "_"),
            categoryId: category?.id ?? null,
            categoryName: category?.name ?? "",
            subcategoryId: subcategory?.id ?? null,
            subcategoryName: subcategory?.name ?? "",
            vendorId: vp.vendor?.id ?? vp.vendor_id,
            vendorName: vp.vendor?.name ?? "Unknown",
            isDefaultVendor: vp.is_default,
            pricingType: vp.pricing_type,
            rate,
            quantity: qty,
            hours: hrs,
            basePrice,
            finalPrice: basePrice,
            isAdjustable: false,
            minPriceLimit: null,
          },
        ],
      };
    });
  };

  const removeItem = (itemId: number) => {
    setForm((prev) => ({
      ...prev,
      vendorServices: prev.vendorServices.filter((vs) => vs.itemId !== itemId),
    }));
  };

  const updateItem = (itemId: number, updates: Partial<VendorServiceItem>) => {
    setForm((prev) => ({
      ...prev,
      vendorServices: prev.vendorServices.map((vs) =>
        vs.itemId === itemId ? { ...vs, ...updates } : vs
      ),
    }));
  };

  // ── Add item (auto-select best vendor) ────────────────────────────────
  const addItemWithBestVendor = (flat: FlatItem) => {
    const { item, service, category, subcategory } = flat;
    if (isItemSelected(item.id)) return;

    const suggestedType = suggestPricingType(item);
    const bestForType = (item.vendor_prices || [])
      .filter(vp => vp.pricing_type === suggestedType)
      .find(vp => vp.is_default);
    const fallback = item.default_vendor_price;
    const bestVP = bestForType || fallback || (item.vendor_prices || [])[0];

    if (bestVP?.vendor) {
      selectVendorForItem(service, category, subcategory, item, bestVP);
    }

    setExpandedItemId(item.id);
  };

  // ── Toggle item expand (to show/change vendor) ────────────────────────
  const toggleItemExpand = (itemId: number) => {
    setExpandedItemId(expandedItemId === itemId ? null : itemId);
  };

  // ── Totals ──────────────────────────────────────────────────────────────
  const vendorServicesTotal = useMemo(
    () => form.vendorServices.reduce((s, vs) => s + vs.finalPrice, 0),
    [form.vendorServices]
  );

  const fieldSx = { "& .MuiInputBase-root": { fontSize: 13 } };

  // ── Render ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rounded" height={60} sx={{ borderRadius: "10px" }} />
        ))}
      </Box>
    );
  }

  if (services.length === 0) {
    return (
      <Alert severity="info" sx={{ borderRadius: "10px" }}>
        No vendor services configured yet. Add services, items, and vendor pricing in the admin panel.
      </Alert>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, borderColor: "#EAE3D9" }}>

      {/* ── Header row ── */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Vendor Catalogue
        </Typography>
        <Badge badgeContent={form.vendorServices.length} color="primary" sx={{ ml: "auto" }}>
          <ShoppingCartIcon color="action" fontSize="small" />
        </Badge>
      </Box>

      {/* ── Search ── */}
      <TextField
        size="small"
        placeholder="Search vendor items…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 1.5, maxWidth: 280, ...fieldSx }}
      />

      {/* ── Category chips ── */}
      <Stack direction="row" spacing={0.5} sx={{ mb: 2, flexWrap: "wrap", rowGap: 0.5 }}>
        {categories.map((cat) => (
          <Chip
            key={cat}
            label={cat}
            size="small"
            clickable
            color={catFilter === cat ? "primary" : "default"}
            variant={catFilter === cat ? "filled" : "outlined"}
            onClick={() => setCatFilter(cat)}
            sx={{ fontSize: 11 }}
          />
        ))}
      </Stack>

      {/* ── Items grid ── */}
      {filtered.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          No items match.
        </Typography>
      ) : (
        <Grid container spacing={1}>
          {filtered.map((flat) => {
            const { item } = flat;
            const selected = getSelectedForItem(item.id);
            const isExpanded = expandedItemId === item.id;
            const vendorPrices = item.vendor_prices || [];
            const defaultVP = item.default_vendor_price;
            const startingPrice = defaultVP ? Number(defaultVP.dcc_price) : null;

            return (
              <Grid item xs={12} sm={6} md={4} key={item.id}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1,
                    borderColor: selected ? "primary.main" : "divider",
                    bgcolor: selected ? "primary.50" : "background.paper",
                    transition: "all 0.15s",
                  }}
                >
                  {/* Item row */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{ flex: 1, minWidth: 0, cursor: "pointer" }}
                      onClick={() => toggleItemExpand(item.id)}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontSize: 12, fontWeight: selected ? 600 : 400, lineHeight: 1.3 }}
                        noWrap
                      >
                        {item.name}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        {selected ? (
                          <>
                            <Typography variant="caption" sx={{ color: "#16a34a", fontWeight: 600, fontSize: 11 }}>
                              {fmtCAD(selected.finalPrice)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                              · {selected.vendorName}
                            </Typography>
                            {selected.isDefaultVendor && (
                              <StarIcon sx={{ fontSize: 10, color: "#d4a373" }} />
                            )}
                          </>
                        ) : startingPrice != null ? (
                          <Typography variant="caption" color="text.secondary">
                            from {fmtCAD(startingPrice)}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            {vendorPrices.length} vendor{vendorPrices.length !== 1 ? "s" : ""}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* Add / Remove button */}
                    {!selected ? (
                      <Tooltip title="Add to booking (auto-selects best vendor)">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => addItemWithBestVendor(flat)}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Remove from booking">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeItem(item.id)}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>

                  {/* ── Inline vendor selection (shown when expanded) ── */}
                  {isExpanded && vendorPrices.length > 0 && (
                    <Box sx={{ mt: 1, pt: 1, borderTop: "1px solid #EAE3D9" }}>

                      {/* Pricing type chips (if multiple) */}
                      {(() => {
                        const availableTypes = Array.from(new Set(vendorPrices.map((vp) => vp.pricing_type)));
                        if (availableTypes.length <= 1) return null;
                        const currentType = selected?.pricingType ?? availableTypes[0];
                        return (
                          <Box sx={{ display: "flex", gap: 0.3, mb: 1, flexWrap: "wrap" }}>
                            {availableTypes.map((pt) => (
                              <Chip
                                key={pt}
                                label={PRICING_TYPE_OPTIONS.find((o) => o.value === pt)?.label ?? pt}
                                size="small"
                                onClick={() => {
                                  if (selected) {
                                    const newVP = vendorPrices.find(
                                      (vp) => vp.vendor_id === selected.vendorId && vp.pricing_type === pt
                                    );
                                    if (newVP) {
                                      selectVendorForItem(flat.service, flat.category, flat.subcategory, item, newVP);
                                    }
                                  }
                                }}
                                sx={{
                                  height: 20, fontSize: 10, fontWeight: 600, cursor: "pointer",
                                  bgcolor: currentType === pt ? "#0f172a" : "#f8f5f0",
                                  color: currentType === pt ? "#fff" : "#6b7280",
                                  border: `1px solid ${currentType === pt ? "#0f172a" : "#e5e7eb"}`,
                                }}
                              />
                            ))}
                          </Box>
                        );
                      })()}

                      {/* Vendor radio list */}
                      {(() => {
                        const activePT = selected?.pricingType ?? vendorPrices[0]?.pricing_type ?? "per_event";
                        const filteredPrices = vendorPrices.filter((vp) => vp.pricing_type === activePT);
                        const pricesToShow = filteredPrices.length > 0 ? filteredPrices : vendorPrices;

                        return pricesToShow
                          .sort((a, b) => (b.is_default ? 1 : 0) - (a.is_default ? 1 : 0) || Number(a.dcc_price) - Number(b.dcc_price))
                          .map((vp) => {
                            if (!vp.vendor) return null;
                            const isVendorSelected = selected?.vendorId === vp.vendor_id && selected?.pricingType === vp.pricing_type;

                            return (
                              <Box
                                key={vp.id}
                                onClick={() => {
                                  if (isVendorSelected) {
                                    removeItem(item.id);
                                  } else {
                                    selectVendorForItem(flat.service, flat.category, flat.subcategory, item, vp);
                                  }
                                }}
                                sx={{
                                  display: "flex", alignItems: "center", gap: 0.8,
                                  px: 1, py: 0.6, mb: 0.4, borderRadius: "8px",
                                  cursor: "pointer",
                                  bgcolor: isVendorSelected ? "#f8f5f0" : "transparent",
                                  border: `1px solid ${isVendorSelected ? "#d4a373" : "transparent"}`,
                                  "&:hover": { bgcolor: "#f8f5f0" },
                                  transition: "all 0.12s",
                                }}
                              >
                                <Radio
                                  checked={isVendorSelected}
                                  size="small"
                                  sx={{
                                    p: 0,
                                    color: "#9ca3af",
                                    "&.Mui-checked": { color: "#d4a373" },
                                  }}
                                />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#0f172a" }} noWrap>
                                      {vp.vendor.name}
                                    </Typography>
                                    {vp.is_default && (
                                      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.2 }}>
                                        <StarIcon sx={{ fontSize: 9, color: "#d4a373" }} />
                                        <Typography sx={{ fontSize: 8, fontWeight: 700, color: "#92710c" }}>Best</Typography>
                                      </Box>
                                    )}
                                  </Box>
                                  {vp.vendor.company_name && (
                                    <Typography sx={{ fontSize: 9, color: "#9ca3af" }} noWrap>
                                      {vp.vendor.company_name}
                                    </Typography>
                                  )}
                                </Box>
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#0f172a", whiteSpace: "nowrap" }}>
                                  {fmtCAD(Number(vp.dcc_price))}
                                </Typography>
                                <Typography sx={{ fontSize: 9, color: "#6b7280" }}>
                                  {PRICING_LABELS[vp.pricing_type] || "/ event"}
                                </Typography>
                              </Box>
                            );
                          });
                      })()}
                    </Box>
                  )}
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* ── Selected vendor items table ── */}
      {form.vendorServices.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, borderColor: "#EAE3D9", mt: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
            Selected Vendor Items
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 700, fontSize: 12 } }}>
                  <TableCell>Item</TableCell>
                  <TableCell>Vendor</TableCell>
                  <TableCell>Pricing</TableCell>
                  <TableCell align="center" sx={{ width: 90 }}>Qty / Hrs</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell sx={{ width: 40 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {form.vendorServices.map((vs) => {
                  const showGuestQty = vs.pricingType === "per_person" || vs.pricingType === "per_plate";
                  const showHours = vs.pricingType === "per_hour";
                  const showUnitQty = vs.pricingType === "per_unit";

                  const handleQtyChange = (qty: number) => {
                    if (qty < 1) return;
                    const base = calculateItemBasePrice(vs.rate, qty, vs.hours, vs.pricingType);
                    updateItem(vs.itemId, { quantity: qty, basePrice: base, finalPrice: base });
                  };

                  const handleHoursChange = (hrs: number) => {
                    if (hrs < 0) return;
                    const base = calculateItemBasePrice(vs.rate, vs.quantity, hrs, vs.pricingType);
                    updateItem(vs.itemId, { hours: hrs, basePrice: base, finalPrice: base });
                  };

                  return (
                    <TableRow key={vs.itemId}>
                      <TableCell sx={{ fontSize: 12 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          {vs.itemName}
                          {vs.isDefaultVendor && (
                            <StarIcon sx={{ fontSize: 10, color: "#d4a373" }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
                        {vs.vendorName}
                      </TableCell>
                      <TableCell sx={{ fontSize: 11, color: "text.secondary" }}>
                        {fmtCAD(vs.rate)} {PRICING_LABELS[vs.pricingType]}
                      </TableCell>
                      <TableCell align="center">
                        {showGuestQty && (
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                            <IconButton size="small" onClick={() => handleQtyChange(vs.quantity - 1)}>
                              <RemoveIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                            <Typography variant="body2" sx={{ minWidth: 20, textAlign: "center", fontSize: 12 }}>
                              {vs.quantity}
                            </Typography>
                            <IconButton size="small" onClick={() => handleQtyChange(vs.quantity + 1)}>
                              <AddIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Box>
                        )}
                        {showHours && (
                          <TextField
                            type="number" size="small" value={vs.hours}
                            onChange={(e) => handleHoursChange(Number(e.target.value))}
                            inputProps={{ min: 0, step: 0.5, style: { width: 50, fontSize: 12, textAlign: "center" } }}
                            variant="standard"
                          />
                        )}
                        {showUnitQty && (
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                            <IconButton size="small" onClick={() => handleQtyChange(vs.quantity - 1)}>
                              <RemoveIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                            <Typography variant="body2" sx={{ minWidth: 20, textAlign: "center", fontSize: 12 }}>
                              {vs.quantity}
                            </Typography>
                            <IconButton size="small" onClick={() => handleQtyChange(vs.quantity + 1)}>
                              <AddIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Box>
                        )}
                        {vs.pricingType === "per_event" && (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 12, fontWeight: 600 }}>
                        {fmtCAD(vs.finalPrice)}
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" color="error" onClick={() => removeItem(vs.itemId)}>
                          <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
                <TableRow sx={{ bgcolor: "action.hover" }}>
                  <TableCell colSpan={4} sx={{ fontSize: 13, fontWeight: 700 }}>
                    Vendor Services Total
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 13, fontWeight: 700 }}>
                    {fmtCAD(vendorServicesTotal)}
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Paper>
  );
};

export default BookingAddOnServices;
